import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CROP_CONFIG } from '../config/cropConfig';
import { Button, ModalFooter, ModalHeader } from './common';

export interface CropData {
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
  cropZoom: number;
}

interface ImageCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onCropConfirm: (cropData: CropData) => void;
  onCancel: () => void;
  initialCropData?: CropData;
  title?: string;
  onRequestNewImage?: () => void;
  onRemoveImage?: () => void;
  canRemoveImage?: boolean;
  hasImage?: boolean;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  imageSrc,
  onCropConfirm,
  onCancel,
  initialCropData,
  title,
  onRequestNewImage,
  onRemoveImage,
  canRemoveImage = false,
  hasImage = true,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('photo.imageEditorTitle', { ns: 'common' });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useState(initialCropData?.cropZoom ?? 1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const img = imageRef.current;

    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, CROP_CONFIG.MODAL_CANVAS_SIZE, CROP_CONFIG.MODAL_CANVAS_SIZE);

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(0, 0, CROP_CONFIG.MODAL_CANVAS_SIZE, CROP_CONFIG.MODAL_CANVAS_SIZE, 8);
    ctx.clip();

    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;

    ctx.drawImage(img, panX, panY, scaledWidth, scaledHeight);
    ctx.restore();

    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    const cropBoxX = (CROP_CONFIG.MODAL_CANVAS_SIZE - CROP_CONFIG.MODAL_CROP_BOX_SIZE) / 2;
    const cropBoxY = (CROP_CONFIG.MODAL_CANVAS_SIZE - CROP_CONFIG.MODAL_CROP_BOX_SIZE) / 2;
    ctx.strokeRect(cropBoxX, cropBoxY, CROP_CONFIG.MODAL_CROP_BOX_SIZE, CROP_CONFIG.MODAL_CROP_BOX_SIZE);
    ctx.setLineDash([]);

    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(0, 0, CROP_CONFIG.MODAL_CANVAS_SIZE, CROP_CONFIG.MODAL_CANVAS_SIZE, 8);
    ctx.stroke();
  }, [panX, panY, zoom]);

  useEffect(() => {
    if (!isOpen) return;

    setImageLoaded(false);
    setZoom(initialCropData?.cropZoom ?? 1);
    setPanX(0);
    setPanY(0);
    setIsDragging(false);

    if (!imageSrc) {
      imageRef.current = null;
      return;
    }

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
    };
    img.onerror = () => {
      imageRef.current = null;
      setImageLoaded(false);
    };
    img.src = imageSrc;
  }, [imageSrc, initialCropData?.cropZoom, isOpen]);

  useEffect(() => {
    if (imageLoaded && initialCropData && imageRef.current) {
      const img = imageRef.current;
      const cropZoom = initialCropData.cropZoom ?? 1;
      const cropX = initialCropData.cropX ?? 0;
      const cropY = initialCropData.cropY ?? 0;

      const scaledWidth = img.width * cropZoom;
      const scaledHeight = img.height * cropZoom;
      const cropBoxX = (CROP_CONFIG.MODAL_CANVAS_SIZE - CROP_CONFIG.MODAL_CROP_BOX_SIZE) / 2;
      const cropBoxY = (CROP_CONFIG.MODAL_CANVAS_SIZE - CROP_CONFIG.MODAL_CROP_BOX_SIZE) / 2;

      setPanX(cropBoxX - cropX * scaledWidth);
      setPanY(cropBoxY - cropY * scaledHeight);
    }
  }, [imageLoaded, initialCropData]);

  useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [drawCanvas, imageLoaded]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const zoomDelta = event.deltaY > 0 ? 0.9 : 1.1;
      setZoom((currentZoom) =>
        Math.max(CROP_CONFIG.MIN_ZOOM, Math.min(CROP_CONFIG.MAX_ZOOM, currentZoom * zoomDelta))
      );
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!imageRef.current) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !imageRef.current) return;

    setPanX(e.clientX - dragStart.x);
    setPanY(e.clientY - dragStart.y);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleReset = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };

  const handleConfirm = () => {
    if (!imageRef.current) return;

    const img = imageRef.current;
    const cropBoxX = (CROP_CONFIG.MODAL_CANVAS_SIZE - CROP_CONFIG.MODAL_CROP_BOX_SIZE) / 2;
    const cropBoxY = (CROP_CONFIG.MODAL_CANVAS_SIZE - CROP_CONFIG.MODAL_CROP_BOX_SIZE) / 2;
    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;
    const cropRelToImageX = cropBoxX - panX;
    const cropRelToImageY = cropBoxY - panY;

    onCropConfirm({
      cropX: Math.max(0, Math.min(1, cropRelToImageX / scaledWidth)),
      cropY: Math.max(0, Math.min(1, cropRelToImageY / scaledHeight)),
      cropWidth: Math.min(1, CROP_CONFIG.MODAL_CROP_BOX_SIZE / scaledWidth),
      cropHeight: Math.min(1, CROP_CONFIG.MODAL_CROP_BOX_SIZE / scaledHeight),
      cropZoom: zoom,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className="mx-4 flex w-full max-w-md flex-col overflow-hidden rounded-[24px] border border-[#E2DDD8] bg-white shadow-[0_24px_60px_rgba(26,23,20,0.24)] max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <ModalHeader
          eyebrow={t('photo.imageEditorTitle', { ns: 'common' })}
          title={resolvedTitle}
          description={
            hasImage
              ? t('photo.existingPhotoSubtitle', { ns: 'common' })
              : t('photo.newPhotoSubtitle', { ns: 'common' })
          }
          onClose={onCancel}
          closeAriaLabel={t('photo.imageEditorCloseAriaLabel', { ns: 'common' })}
        />

        {imageLoaded ? (
          <>
            <div className="overflow-y-auto">
              <div className="space-y-4 px-6 py-5">
                <div className="flex justify-center">
                  <canvas
                    ref={canvasRef}
                    width={CROP_CONFIG.MODAL_CANVAS_SIZE}
                    height={CROP_CONFIG.MODAL_CANVAS_SIZE}
                    className="cursor-move rounded-lg border-2 border-gray-300 bg-gray-50"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  />
                </div>

                <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                  <p className="mb-1 font-semibold">{t('photo.howToUseTitle', { ns: 'common' })}</p>
                  <ul className="list-inside list-disc space-y-1">
                    <li>{t('photo.howToUseScrollZoom', { ns: 'common' })}</li>
                    <li>{t('photo.howToUseDragPan', { ns: 'common' })}</li>
                    <li>{t('photo.howToUsePosition', { ns: 'common' })}</li>
                  </ul>
                </div>

                <div className="flex gap-2" onMouseDown={(e) => e.stopPropagation()}>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setZoom((currentZoom) => Math.max(CROP_CONFIG.MIN_ZOOM, currentZoom - 0.2));
                    }}
                    variant="secondary"
                    className="flex-1"
                  >
                    {t('photo.zoomOut', { ns: 'common' })}
                  </Button>
                  <div className="flex flex-1 items-center justify-center rounded bg-gray-100 px-3 py-2 text-sm font-medium">
                    {(zoom * 100).toFixed(0)}%
                  </div>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setZoom((currentZoom) => Math.min(CROP_CONFIG.MAX_ZOOM, currentZoom + 0.2));
                    }}
                    variant="secondary"
                    className="flex-1"
                  >
                    {t('photo.zoomIn', { ns: 'common' })}
                  </Button>
                </div>
              </div>
            </div>

            {/* Footer — two rows to avoid overflow */}
            <div className="shrink-0 border-t border-[#E2DDD8] bg-white px-6 py-4 space-y-3">
              {/* Secondary actions row — only rendered when applicable */}
              {(onRequestNewImage || (canRemoveImage && onRemoveImage)) && (
                <div className="flex gap-2" onMouseDown={(e) => e.stopPropagation()}>
                  {onRequestNewImage && (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onRequestNewImage();
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('photo.replacePhoto', { ns: 'common' })}
                    </Button>
                  )}
                  {canRemoveImage && onRemoveImage && (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onRemoveImage();
                      }}
                      variant="secondary"
                      className="flex-1 border-[#E9B6B0] bg-[#FCEBEB] text-[#A32D2D] hover:bg-[#F8DDDD] hover:text-[#A32D2D]"
                    >
                      {t('photo.removePhoto', { ns: 'common' })}
                    </Button>
                  )}
                </div>
              )}

              {/* Primary actions row */}
              <div className="flex gap-2" onMouseDown={(e) => e.stopPropagation()}>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleReset();
                  }}
                  variant="outline"
                >
                  {t('photo.reset', { ns: 'common' })}
                </Button>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onCancel();
                  }}
                  variant="outline"
                  className="ms-auto"
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleConfirm();
                  }}
                >
                  {t('photo.saveImage', { ns: 'common' })}
                </Button>
              </div>
            </div>
          </>
        ) : hasImage ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="text-gray-600">Loading image...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6 px-6 py-6">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRequestNewImage?.();
              }}
              disabled={!onRequestNewImage}
              className="w-full rounded-2xl border border-dashed border-[#C8D6EA] bg-[#F7FAFF] px-6 py-10 text-center transition hover:border-[#185FA5] hover:bg-[#EEF4FB] disabled:cursor-default disabled:opacity-50"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#185FA5] text-white shadow-[0_10px_22px_rgba(24,95,165,0.22)]">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 15V7" />
                  <path d="M8.8 10.2L12 7l3.2 3.2" />
                  <path d="M5 16.5v1A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-1" />
                </svg>
              </div>
              <p className="mt-4 text-base font-semibold text-[#1A1714]">{t('photo.noPhotoSelected', { ns: 'common' })}</p>
              <p className="mt-1 text-sm text-gray-600">{t('photo.uploadPrompt', { ns: 'common' })}</p>
            </button>

            <ModalFooter className="px-0 pb-0 [border-top-width:0px]">
              {onRequestNewImage && (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRequestNewImage();
                  }}
                  className="flex-1"
                >
                  {t('photo.uploadLabel', { ns: 'common' })}
                </Button>
              )}
              <Button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onCancel();
                }}
                variant="outline"
                className="flex-1"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
            </ModalFooter>
          </div>
        )}
      </div>
    </div>
  );
};
